<template>
    <AccordionRoot
        v-model="model"
        :type="type"
        :collapsible="type === 'single' ? collapsible : undefined"
        :disabled="disabled"
        :class="block()"
    >
        <AccordionItem
            v-for="(item, index) in items"
            :key="item.value"
            :value="item.value"
            :disabled="item.disabled"
            :class="element('item')"
        >
            <AccordionHeader
                :as="`h${headingLevel}`"
                :class="element('header')"
            >
                <AccordionTrigger :class="element('trigger')">
                    <span :class="element('title')">
                        <slot
                            name="title"
                            :item="item"
                            :index="index"
                        >
                            {{ item.title }}
                        </slot>
                    </span>
                    <span
                        :class="element('icon')"
                        aria-hidden="true"
                    >
                        <slot name="icon">
                            <svg
                                viewBox="0 0 16 16"
                                fill="none"
                                focusable="false"
                            >
                                <path
                                    d="m3 6 5 5 5-5"
                                    stroke="currentColor"
                                    stroke-width="1.75"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </slot>
                    </span>
                </AccordionTrigger>
            </AccordionHeader>

            <AccordionContent
                force-mount
                :class="element('content')"
            >
                <div
                    :class="element('body')"
                    :hidden="!isItemOpen(item.value)"
                >
                    <slot
                        :item="item"
                        :index="index"
                    />
                </div>
            </AccordionContent>
        </AccordionItem>
    </AccordionRoot>
</template>

<script setup lang="ts">
import type { KAccordionItem, KAccordionProps } from './types'
import useBem from '@yeeroen/use-bem'
import {
    AccordionContent,
    AccordionHeader,
    AccordionItem,
    AccordionRoot,
    AccordionTrigger,
} from 'reka-ui'

defineOptions({ name: 'KAccordion' })

withDefaults(defineProps<KAccordionProps>(), {
    items: () => [],
    type: 'multiple',
    collapsible: true,
    disabled: false,
    headingLevel: 3,
})

defineSlots<{
    default?: (props: { item: KAccordionItem, index: number }) => unknown
    title?: (props: { item: KAccordionItem, index: number }) => unknown
    icon?: () => unknown
}>()

const model = defineModel<string | string[]>()
const { block, element } = useBem('k-accordion')

function isItemOpen(value: string) {
    return Array.isArray(model.value) ? model.value.includes(value) : model.value === value
}
</script>

<style lang="scss">
@use './k-accordion.scss';
</style>
